/**
 * Who is running a session that is already under way.
 *
 * Both the therapist's calendar and the supervisor's can start the same
 * session. Once it is running it belongs to whoever pressed Start: only they
 * may collect data against it or end it. The backend enforces this on
 * POST /sessions/{id}/end; these helpers keep the UI from offering actions
 * that would come back a 403.
 */

/**
 * The person entitled to run this session.
 *
 * Sessions started before `started_by` existed carry null there. For those we
 * fall back to the assigned therapist — who would have started it in practice
 * — so a historical in-progress session stays finishable rather than stranded.
 * This mirrors _session_runner_id in backend/app/api/v1/sessions.py; change
 * both together.
 *
 * @param {Object} session - transformed session (see transformSession)
 * @returns {string|null} person id, or null if neither is known
 */
export const sessionRunnerId = (session) => session?.startedBy || session?.therapistId || null;

/**
 * Whether this viewer is the one running the session.
 *
 * @param {Object} session - transformed session
 * @param {Object} user - the logged-in user (needs person_id)
 * @returns {boolean}
 */
export const canRunSession = (session, user) => {
  const runnerId = sessionRunnerId(session);
  const viewerId = user?.person_id || null;
  if (!runnerId || !viewerId) return false;
  return runnerId === viewerId;
};

/**
 * Why the running actions are unavailable, for a tooltip. Empty string when
 * they are available, so it can be dropped straight into a Tooltip title.
 *
 * @param {Object} session - transformed session
 * @param {Object} user - the logged-in user
 * @returns {string}
 */
export const runnerBlockedReason = (session, user) => {
  if (!canRunSession(session, user)) {
    // Distinguish "not yours" from "we do not know who you are". The second happens on
    // a restored session that never recovered person_id, and telling the user somebody
    // else is running their own session is worse than saying nothing useful — it sends
    // them looking for a colleague who does not exist.
    return user?.person_id
      ? 'This session is being run by someone else — only they can collect data or end it.'
      : 'Your profile is still loading. Reload the page if this does not clear.';
  }
  // The actions are also gated on a pre-session note, and blaming the runner rule for
  // that left two dead buttons with no explanation anywhere on the screen.
  if (!(session?.notes || '').trim()) {
    return 'Add a pre-session note first — use Start/Edit note above.';
  }
  return '';
};
